import * as Accordion from "#root/components/accordion";
import { GlyphLoading } from "#root/components/glyphs";
import Sidebar from "#root/components/sidebar";
import { WithSidebar } from "#root/components/withSidebar";
import { getMonthlyUsageCollectionForDeployment } from "#root/schema/usage";
import { useData } from "#root/utility/hooks";
import { getMonthList, monthNames } from "#root/utility/months";
import formatNumber from "cloudquote-number-format";
import type { ReactNode } from "react";
import { Fragment, useMemo } from "react";
import { Link } from "react-router-dom";
import PageLayout from "./layout";

export default function UsagePage({ accountId, deploymentId, ymd }): ReactNode {
  return (
    <PageLayout title="Usage">
      <WithSidebar>
        <UsageSidebar accountId={accountId} deploymentId={deploymentId} ymd={ymd}/>
        <section>
          {ymd ? (
            <UsageTable accountId={accountId} deploymentId={deploymentId} ymd={ymd}/>
          ) : (
            <Fragment>
              <h2>API Usage</h2>
              <p>Please select a month to view API Usage</p>
            </Fragment>
          )}
        </section>
      </WithSidebar>
    </PageLayout>
  );
}

function UsageSidebar({ ymd, accountId, deploymentId }: { accountId: string; deploymentId: string; ymd: string }) {
  const months = useMemo(() => getMonthList(), []);
  return (
    <Sidebar>
      <Accordion.Parent initialItemId={ymd} initialGroupId="By Month">
        <Accordion.Group label="By Month">
          {months.map(({ ymd, name }) => (
            <Accordion.Item key={ymd} itemId={ymd}>
              <Link to={`/admin/account/${accountId}/deployment/${deploymentId}/usage/month/${ymd}`}>{name}</Link>
            </Accordion.Item>
          ))}
        </Accordion.Group>
      </Accordion.Parent>
    </Sidebar>
  );
}

function UsageTable({ accountId, deploymentId, ymd }: { accountId: string; deploymentId: string; ymd: string }) {
  const [details] = useData(async () => {
    const { rows } = await getMonthlyUsageCollectionForDeployment(accountId, deploymentId, ymd).list();

    return rows.filter(row => !row.unit.startsWith("Error"));
  }, [ymd]);

  if (!details) {
    return <GlyphLoading/>;
  }

  const date = new Date(ymd);

  return (
    <div className="usage-table">
      <h1>
        {monthNames[date.getUTCMonth()]} {date.getUTCFullYear()} - API Usage
      </h1>
      <table className="table table-striped">
        <thead>
        <tr>
          <th>Product Code</th>
          <th>Unit</th>
          <th>Count</th>
        </tr>
        </thead>
        <tbody>
        {details.map((row, i) => (
          <tr key={`${ymd}-${i}`}>
            <td>{row.name}</td>
            <td>{row.unit}</td>
            <td>{formatNumber(row.requests, { Comma: true })}</td>
          </tr>
        ))}
        </tbody>
      </table>
    </div>
  );
}
